
import { Component, Vue, Prop } from 'vue-property-decorator';
import ChatConversation from './ChatConversation.vue';
import ChatHistory from './ChatHistory.vue';
import AgentBrowser from './AgentBrowser.vue';
import ErrorBoundary from './ErrorBoundary.vue';
import AgentsCollection, { AgentResponse } from '@/collections/agents';
import ChatCollection, { ChatMessage as ApiChatMessage, ChatMessageRequest, ChatMessageResponse, ChatMetadata } from '@/collections/chat';
import { EventBus } from '@/main';
import { contextSyncService } from '@/services/contextSync';
import {
  executeAgentAction,
  agentActionHandlers,
  getCanvasActionTypes,
  isCanvasAction,
} from '@/config/agentActions';
import { ChatContext } from '@/models/agentActions';
import { ReportBuilderEventHandler } from '@/modules/reportBuilderEvents';
import type { StreamingEvent, StreamingMetadata, StreamingEventMetadata } from '@/types/agents';
import {
  responseProcessorRegistry,
  ProcessorContext,
  ProcessingResult,
  NormalizedResponse,
} from '@/services/responseProcessors';

export interface ChatMessage {
  id: string;
  content: string;
  timestamp: Date;
  isUser: boolean;
  agent?: {
    name: string;
    avatar: string;
  };
  structuredContent?: any[];  // Array of content items from API
  isStreaming?: boolean;  // Flag to indicate message is currently streaming
}

export interface IChatConversation {
  id: string;
  title: string;
  lastMessage: string;
  timestamp: Date;
  messages: ChatMessage[];
  agentId?: string;
  agentName?: string;
}

export interface Agent {
  id: string;
  name: string;
  description: string;
  avatar: string;
  category: string;
  isRecommended: boolean;
  isFavorite: boolean;
  creator: string;
}

// Helper function to convert API response to Agent interface
function mapAgentResponse(response: AgentResponse): Agent {
  return {
    id: response.id,
    name: response.name,
    description: response.description,
    avatar: 'mdi-assistant', // Use consistent assistant icon for all agents
    category: response.category || 'All Agents',
    isRecommended: response.isRecommended || false,  // Backend uses camelCase
    isFavorite: response.isFavorite || false,        // Backend uses camelCase
    creator: response.creator || 'Unknown',
  };
}

@Component({
  components: {
    ChatConversation,
    ChatHistory,
    AgentBrowser,
    ErrorBoundary,
  },
})
export default class AgentChatbox extends Vue {
  @Prop({ default: false })
  public isFullscreen!: boolean;

  public activeTab: number = 0;
  public chatLoading: boolean = false;
  public agentsLoading: boolean = false;
  public agentsError: string | null = null;
  public historySearchQuery: string = '';
  public currentConversationId: string = '';
  public isContinuedConversation: boolean = false;

  // Current selected agent for chat context
  public selectedAgent: Agent | null = null;

  // Sample data - to be replaced with API calls
  public currentConversation: ChatMessage[] = [

  ];

  public hasUnsavedMessage: boolean = false;
  public pendingMessage: string = '';
  public showNavigationWarning: boolean = false;
  public pendingNavigationCallback: (() => void) | null = null;

  public chatHistory: IChatConversation[] = [

  ];

  public availableAgents: Agent[] = [];

  // Request deduplication and state tracking
  private isProcessing: boolean = false;
  private currentRequestId: number = 0;
  private lastMessage: string = '';
  private handoffInProgress: boolean = false;

  // NEW: Streaming state for SSE events (Phase 2)
  private currentToolName: string | null = null;
  private isToolExecuting: boolean = false;
  private streamingEvents: StreamingEvent[] = [];

  // PO Agent context sync: displayed products from BuyingTableDisplay
  private poDisplayedProducts: Array<Record<string, any>> = [];
  // PO Agent context sync: discussed products from PurchaseOrderDisplay (for "copy items from PO" workflow)
  private poDiscussedProducts: Array<Record<string, any>> = [];
  // Check if user is logged in from Hades (internal team) - used to show/hide beta features
  private isLoggedInFromHades: boolean = localStorage.getItem('loggedInFromHades') === 'true';
  private streamingMetadata: StreamingMetadata | null = null;

  // Computed property for tool indicator text
  get toolIndicatorText(): string {
    if (!this.isToolExecuting || !this.currentToolName) {
      return '';
    }

    // Map tool names to user-friendly messages
    const toolMessages: Record<string, string> = {
      file_search: 'Searching knowledge base...',
      web_search: 'Searching the web...',
      get_order_details: 'Retrieving order information...',
      get_product_info: 'Looking up product details...',
      check_inventory: 'Checking inventory levels...',
    };

    return toolMessages[this.currentToolName] || `Executing ${this.currentToolName}...`;
  }

  // Computed property to filter out empty/meaningless messages
  get filteredConversation(): ChatMessage[] {
    return this.currentConversation.filter(message => {
      // Don't filter user messages
      if (message.isUser) {
        return true;
      }

      // Only filter out temporary streaming placeholders that are empty
      // Once streaming completes (isStreaming = false), show the message even if empty
      const trimmedContent = message.content.trim();
      const isEmpty = !trimmedContent || trimmedContent === '{}' || trimmedContent === '[]';

      if (isEmpty && message.isStreaming) {
        return false; // Hide empty placeholders during streaming
      }

      return true; // Show completed messages even if empty
    });
  }

  /**
   * Check if the currently selected agent is a PO (Purchase Order) agent
   */
  private isPOAgent(): boolean {
    return this.selectedAgent?.id === 'purchase-order' || this.selectedAgent?.id === 'po-agent';
  }

  // Track action loading messages for cleanup
  private actionLoadingMessageIds: Map<string, string> = new Map(); // action name -> message ID

  // Phase 2: Conversation tracking for analytics
  private conversationStartTime: number = 0;

  // Store bound event handler for proper cleanup
  private reportBuilderQueryHandler: any;

  // Report Builder event tracking
  private reportBuilderEventHandler: ReportBuilderEventHandler | null = null;

  // API collection instances
  private agentsCollection: AgentsCollection = new AgentsCollection();
  private chatCollection: ChatCollection = new ChatCollection();
  private readyEmitted: boolean = false;

  // Feature flag for new response processor architecture
  // Enable via localStorage: localStorage.setItem('use_response_processors', 'true')
  private useResponseProcessors: boolean = localStorage.getItem('use_response_processors') === 'true';

  public async created() {
    await this.loadAgents();

    // Initialize Report Builder event handler with error handling
    try {
      this.reportBuilderEventHandler = new ReportBuilderEventHandler();
    } catch (error) {
      this.reportBuilderEventHandler = null;
      // Component continues operating without event handler
    }

    // Set chatbox open status in localStorage
    localStorage.setItem('assisty_chatbox_open', 'true');

    // Try to restore previous session from localStorage
    const restored = await this.restorePreviousSession();

    if (!restored) {
      // If no session restored, start fresh
      this.currentConversationId = this.generateConversationId();
      // Set session ID for context sync service
      contextSyncService.setSessionId(this.currentConversationId);

      // Try to restore the last selected agent
      const restoredAgent = await this.restoreLastSelectedAgent();

      if (!restoredAgent) {
        // Only auto-select default if no agent was previously selected
        await this.autoSelectOrchestrator('customer-service');
      }
    }

    // Listen for programmatic agent selection and message sending
    EventBus.$on('chatbox:select-agent', this.selectAgentById);
    EventBus.$on('chatbox:send-message', this.sendMessageProgrammatically);

    // Listen for chatbox status check requests
    EventBus.$on('chatbox:check-status', this.handleStatusCheck);

    // Listen for Report Builder compound commands - bind context properly
    this.reportBuilderQueryHandler = (payload: any) => {
      this.handleReportBuilderQuery(payload);
    };
    EventBus.$on('chatbox:report-builder-query', this.reportBuilderQueryHandler);

    // Listen for PO Agent products loaded event for context sync
    EventBus.$on('po-builder:products-loaded', this.handlePOProductsLoaded);

    // Listen for PO Agent discussed products loaded event (from PO detail view)
    EventBus.$on('po-builder:discussed-products-loaded', this.handlePODiscussedProductsLoaded);

    // Emit chatbox:ready event after all initialization is complete
    // This ensures synchronization with components waiting for the chatbox to be ready

    this.$nextTick(() => {
      if (!this.readyEmitted) {
        this.readyEmitted = true;
        EventBus.$emit('chatbox:ready');
      }
    });
  }

  public async sendMessage(message: string, retryCount: number = 0, metadata?: Record<string, any>) {
    // Route to new processor-based method when feature flag is enabled
    if (this.useResponseProcessors) {
      return this.sendMessageWithProcessors(message, retryCount, metadata);
    }

    // Prevent multiple simultaneous requests
    if (this.isProcessing) {
      return;
    }
    // Check if an agent is selected
    if (!this.selectedAgent) {
      // Add system message prompting user to select an agent
      const systemMessage: ChatMessage = {
        id: this.generateMessageId(),
        content: 'Please select an agent from the Agents tab before starting a conversation.',
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: 'System',
          avatar: 'mdi-information',
        },
      };
      this.currentConversation.push(systemMessage);
      return;
    }
    // Mark as processing and create unique request ID
    this.isProcessing = true;
    const requestId = Date.now();
    this.currentRequestId = requestId;

    // Store last message for retry
    this.lastMessage = message;

    // Proactively clear any stale streaming state from previous attempts
    // This ensures the UI is clean before starting a new message
    this.isToolExecuting = false;
    this.currentToolName = null;
    this.streamingEvents = [];
    this.streamingMetadata = null;

    // Add user message to UI
    const userMessage: ChatMessage = {
      id: this.generateMessageId(),
      content: message,
      timestamp: new Date(),
      isUser: true,
    };
    this.currentConversation.push(userMessage);

    // Track query submission for report-builder agent (Phase 2)
    if (this.selectedAgent.id === 'report-builder' && this.reportBuilderEventHandler) {
      const userMessages = this.currentConversation.filter((m) => m.isUser);
      this.reportBuilderEventHandler.trackQuerySubmitted({
        queryText: message,
        agentId: this.selectedAgent.id,
        agentName: this.selectedAgent.name,
        conversationType: userMessages.length === 1 ? 'direct_query' : 'conversational',
        messageNumber: userMessages.length,
        sessionId: this.currentConversationId,
      });
    }

    // Send message to API and get response (non-streaming)
    this.chatLoading = true;

    try {
      // Build metadata - merge passed metadata with context
      let requestMetadata: Record<string, any> = metadata ? { ...metadata } : {};

      // Always attach current screen context for ALL agents (automatic context sync)
      const currentContext = contextSyncService.getCurrentContext();
      if (currentContext?.screen) {
        requestMetadata.context = {
          ...requestMetadata.context,
          screen: currentContext.screen,
        };
      }

      // Attach filter context for PO agent (e.g., active performance filter on buying table)
      if (this.isPOAgent() && currentContext?.filters) {
        requestMetadata.context = {
          ...requestMetadata.context,
          filters: currentContext.filters,
        };
      }

      // For PO agent, automatically include displayed products for context sync
      if (this.isPOAgent() && this.poDisplayedProducts.length > 0) {
        requestMetadata.displayed_products = this.poDisplayedProducts;
      }

      // For PO agent, include discussed products (from viewing PO detail)
      if (this.isPOAgent() && this.poDiscussedProducts.length > 0) {
        requestMetadata.discussed_products = this.poDiscussedProducts;
      }

      // Prepare API request - always include session_id for conversation continuity
      const messageRequest: ChatMessageRequest = {
        message,
        session_id: this.currentConversationId,
        // Include metadata if provided (for context sync)
        ...(Object.keys(requestMetadata).length > 0 && { metadata: requestMetadata }),
      };

      // Send to Atlas backend API (non-streaming)
      const response = await this.chatCollection.sendMessage(
        this.selectedAgent.id,
        messageRequest,
      );

      // Extract message content from response
      let messageContent = '';

      // Check for messages at top level first (PO Agent format)
      const responseAny = response as any;
      if (responseAny.messages && Array.isArray(responseAny.messages) && responseAny.messages.length > 0) {
        // Canvas action types that should be filtered from chat display
        const canvasActionTypes = getCanvasActionTypes();

        const messageItems = responseAny.messages
          .filter((msg: any) => {
            if (!msg.content || typeof msg.content !== 'string') {
              return false;
            }

            // Try to parse as JSON to check if it's an action/control message
            try {
              const parsed = JSON.parse(msg.content);

              // If it parses as JSON and has an "action" field, check if it's a canvas action
              if (parsed.action && isCanvasAction(parsed.action)) {
                return false;
              }

              // If it has action or parameters fields, it's likely a control message
              if (parsed.action || parsed.parameters) {
                return false;
              }

              // If it's a JSON object (not a string), exclude it
              if (typeof parsed === 'object' && parsed !== null) {
                return false;
              }
            } catch (e) {
              // Not JSON, check for legacy string-based control messages
              const isLegacyControlMessage = canvasActionTypes.some((actionType: string) =>
                msg.content.includes(actionType)
              );
              if (isLegacyControlMessage) {
                return false;
              }
            }

            // This is a regular text message
            return true;
          })
          .map((msg: any) => msg.content);
        messageContent = messageItems.join('\n\n');
      } else if (typeof response.reply === 'object' && response.reply !== null) {
        const replyObj = response.reply as any;

        // Check for new ChatResponse format (messages/events/context in reply object)
        if ('messages' in replyObj && Array.isArray(replyObj.messages) && replyObj.messages.length > 0) {
          // Canvas action types that should be filtered from chat display
          const canvasActionTypesInner = getCanvasActionTypes();

          // Apply same filtering logic as top-level messages
          const filteredMessages = replyObj.messages
            .filter((msg: any) => {
              if (!msg.content || typeof msg.content !== 'string') {
                return false;
              }

              // Try to parse as JSON to check if it's an action/control message
              try {
                const parsed = JSON.parse(msg.content);

                // If it parses as JSON and has an "action" field, check if it's a canvas action
                if (parsed.action && isCanvasAction(parsed.action)) {
                  return false;
                }

                // If it has action or parameters fields, it's likely a control message
                if (parsed.action || parsed.parameters) {
                  return false;
                }

                // If it's a JSON object (not a string), exclude it
                if (typeof parsed === 'object' && parsed !== null) {
                  return false;
                }
              } catch (e) {
                // Not JSON, check for legacy string-based control messages
                const isLegacyControlMessage = canvasActionTypesInner.some((actionType: string) =>
                  msg.content.includes(actionType)
                );
                if (isLegacyControlMessage) {
                  return false;
                }
              }

              // This is a regular text message
              return true;
            })
            .map((msg: any) => msg.content);

          messageContent = filteredMessages.join('\n\n');
        } else if ('items' in replyObj) {
          // Old structured format with items - extract text
          const items = replyObj.items || [];
          messageContent = items.map((item: any) => {
            if (typeof item.content === 'string') {
              return item.content;
            }
            return '';
          }).join('\n\n');
        } else {
          // Fallback: convert to string
          messageContent = String(response.reply);
        }
      } else {
        // Plain string reply
        messageContent = String(response.reply);
      }
      // Filter out [HANDOFF_SIGNAL] variations from content
      messageContent = messageContent.replace(/\[HANDOFF_?SIGNAL\]/g, '').trim();

      // Create assistant message with complete content
      const assistantMessageId = this.generateMessageId();
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        content: messageContent,
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: this.selectedAgent.name,
          avatar: this.selectedAgent.avatar,
        },
        isStreaming: false,
      };
      this.currentConversation.push(assistantMessage);

      // Turn off loading indicator
      this.chatLoading = false;

      // Update session_id if it's a new conversation (backend might return a new session_id)
      if (response.session_id && response.session_id !== this.currentConversationId) {
        this.currentConversationId = response.session_id;
      }

      // Detect response format once and branch accordingly
      const isNewFormat = this.isNewChatResponseFormat(response);

      if (isNewFormat) {
        // Extract events and metadata from response.reply
        const chatResponse = response.reply as any;
        const events = chatResponse.events || [];

        // Process events for preview_canvas_action and human_handoff
        this.processNewFormatEvents(events, chatResponse);
      }
      // Handle metadata using action-based pattern (old format only)
      if (!isNewFormat && response.metadata) {
        // Check for new action-based pattern first
        if (response.metadata.action) {
          const actionName = response.metadata.action;
          // Validate payload is an object before use, fallback to metadata
          const rawPayload = response.metadata.payload;
          const actionPayload = (rawPayload && typeof rawPayload === 'object' ? rawPayload : response.metadata) as Record<string, any>;
          const actionHandler = agentActionHandlers[actionName];

          // Show loading message if the action has one defined
          let loadingMessageId: string | null = null;
          if (actionHandler && actionHandler.loadingMessage) {
            loadingMessageId = this.generateMessageId();
            const loadingMessage: ChatMessage = {
              id: loadingMessageId,
              content: actionHandler.loadingMessage,
              timestamp: new Date(),
              isUser: false,
              agent: {
                name: this.selectedAgent?.name || 'System',
                avatar: this.selectedAgent?.avatar || 'mdi-cog',
              },
              isStreaming: true, // Show loading indicator
            };

            this.currentConversation.push(loadingMessage);
            this.actionLoadingMessageIds.set(actionName, loadingMessageId);

            // Force Vue to update
            this.$forceUpdate();

            // Listen for completion event if defined
            if (actionHandler.completionEvent) {
              const completionHandler = () => {
                // Add minimum display time of 1 second
                setTimeout(() => {
                  this.removeActionLoadingMessage(actionName);
                }, 1000);
                EventBus.$off(actionHandler.completionEvent!, completionHandler);
              };
              EventBus.$once(actionHandler.completionEvent, completionHandler);
            }
          }

          // Execute the action after a small delay to ensure loading message is visible
          // Check if this is a report preview action and handle navigation
          if (actionName === 'show_report_preview') {
            // Extract parameters could be in payload or directly in metadata
            const extractedParams = actionPayload.extracted_parameters || actionPayload;
            this.handleReportPreviewAction(extractedParams, {
              sessionId: this.currentConversationId,
              agentId: this.selectedAgent?.id || 'unknown',
              userId: (this as any).$store?.state?.user?.id || 'anonymous',
            });

            // Track conversation quality for report-builder agent (Phase 5)
            if (this.selectedAgent?.id === 'report-builder') {
              const userMessages = this.currentConversation.filter((m) => m.isUser);
              // Use robust clarification detection to avoid false positives
              const clarificationMessages = this.currentConversation.filter((m) =>
                this.isClarificationRequest(m),
              );

              EventBus.$emit('report-builder:conversation-quality', {
                agentId: this.selectedAgent.id,
                sessionId: this.currentConversationId,
                messageCount: this.currentConversation.length,
                clarificationRequestsCount: clarificationMessages.length,
                successfulInterpretation: true,
                conversationDuration: this.conversationStartTime ? (Date.now() - this.conversationStartTime) / 1000 : 0,
                queryRefinementIterations: userMessages.length - 1,
              });
            }
          } else {
            // Execute other actions immediately
            setTimeout(() => {
              const context: ChatContext = {
                sessionId: this.currentConversationId,
                agentId: this.selectedAgent?.id || 'unknown',
                userId: (this as any).$store?.state?.user?.id || 'anonymous',
              };
              executeAgentAction(actionName, actionPayload, context);
            }, 100);
          }

          // If no completion event is defined, remove loading message after delay
          if (loadingMessageId && (!actionHandler || !actionHandler.completionEvent)) {
            // Remove after a longer delay to show the loading state
            setTimeout(() => {
              this.removeActionLoadingMessage(actionName);
            }, 2000);
          }
        } else if (this.selectedAgent?.id === 'report-builder' &&
                 (response.metadata.parameters || response.metadata.extracted_parameters ||
                  response.metadata.measure || response.metadata.report_name)) {

          // Extract parameters from either direct metadata or nested extracted_parameters/payload
          // Validate payload structure before accessing nested properties
          const payloadParams = response.metadata.payload &&
                               typeof response.metadata.payload === 'object' &&
                               'extracted_parameters' in response.metadata.payload
                                 ? (response.metadata.payload as any).extracted_parameters
                                 : undefined;
          const extractedParams = response.metadata.extracted_parameters ||
                                 payloadParams ||
                                 response.metadata;

          // Format the payload for the report preview
          const reportPayload = {
            parameters: extractedParams.parameters || response.metadata.parameters || response.metadata,
            report_name: extractedParams.report_name || response.metadata.report_name,
            confidence_score: extractedParams.confidence_score || response.metadata.confidence_score,
            suggested_questions: extractedParams.suggested_questions || response.metadata.suggested_questions,
            sample_data: extractedParams.sample_data,  // Include sample data for onboarding mode
            is_sample_data: extractedParams.is_sample_data,  // Include sample data flag
            success: true,
          };
          // Emit the preview event directly
          EventBus.$emit('report-builder:preview', reportPayload);

          // Emit conversation quality tracking (Phase 2)
          // Count user messages and clarification requests from conversation
          const userMessages = this.currentConversation.filter((m) => m.isUser);
          // Use robust clarification detection to avoid false positives
          const clarificationMessages = this.currentConversation.filter((m) =>
            this.isClarificationRequest(m),
          );

          EventBus.$emit('report-builder:conversation-quality', {
            agentId: this.selectedAgent.id,
            sessionId: this.currentConversationId,
            messageCount: this.currentConversation.length,
            clarificationRequestsCount: clarificationMessages.length,
            successfulInterpretation: true,
            conversationDuration: this.conversationStartTime ? (Date.now() - this.conversationStartTime) / 1000 : 0,
            queryRefinementIterations: userMessages.length - 1, // First message is the initial query
          });
        }

        // Fallback to legacy handler for backwards compatibility
        if (response.metadata.needs_human_handoff) {
          this.checkForHandoffRequest(response);
        }
      }

      // Post-process the assistant message
      const messageIndex = this.currentConversation.findIndex((m) => m.id === assistantMessageId);
      // Note: PO Agent actions are now handled exclusively through the events array
      // in processNewFormatEvents() - no need to parse messages for actions

      // Finalize message - remove streaming flag and check for routing
      const finalMessageIndex = this.currentConversation.findIndex((m) => m.id === assistantMessageId);
      if (finalMessageIndex !== -1) {
        const finalMessage = this.currentConversation[finalMessageIndex];
        let needsUpdate = false;

        // Clean up any remaining handoff signal from displayed content
        if (finalMessage.content.includes('[HANDOFF_SIGNAL]')) {
          finalMessage.content = "I understand that you need to speak to a human. I'll connect you with our support team.";
          needsUpdate = true;
        }

        // Clean up canvas action control messages from chat display (applies to all agents)
        const canvasActionsToClean = getCanvasActionTypes();
        // Add any known variations that might not be in the registry
        const allControlMessages = [...canvasActionsToClean, 'DISPLAY_SUPPLIERSLIST'];

        for (const controlMsg of allControlMessages) {
          if (finalMessage.content.includes(controlMsg)) {
            finalMessage.content = finalMessage.content.replace(new RegExp(controlMsg, 'g'), '').trim();
          }
        }

        // If the message is empty AND we had an action, remove the empty message
        // This happens when the agent only returns metadata/action without text content
        const hadAction = response.metadata && response.metadata.action;

        if (!finalMessage.content.trim() && hadAction) {
          this.currentConversation.splice(messageIndex, 1);
        } else {
          // Check if the response is a routing directive
          const routingMatch = finalMessage.content.match(/^\[ROUTE:([^\]]+)\](.*)$/);

          if (routingMatch) {
            // This is a routing response - update content and switch agent
            const targetAgentId = routingMatch[1];
            const routingMessage = routingMatch[2] || `Routing to ${targetAgentId} specialist...`;
            finalMessage.content = routingMessage;
            needsUpdate = true;

            // Switch to the target agent
            await this.switchToAgent(targetAgentId);
          }

          // Only trigger Vue update if content was modified
          if (needsUpdate) {
            this.$set(this.currentConversation, finalMessageIndex, { ...finalMessage });
          }
        }
      }

      // Refresh chat history to include the new conversation
      this.refreshChatHistory();

      // Save session to localStorage for persistence
      this.saveSessionToStorage();
    } catch (error: any) {
      // Retry logic for network errors (max 2 retries)
      if (retryCount < 2 && this.isRetryableError(error)) {
        // Remove the user message from UI since we'll re-add it on retry
        this.currentConversation.pop();
        setTimeout(() => {
          this.sendMessage(message, retryCount + 1);
        }, 1000 * (retryCount + 1)); // Exponential backoff
        return;
      }

      // Add error message to UI
      const errorMessage: ChatMessage = {
        id: this.generateMessageId(),
        content: this.getErrorMessage(error, retryCount >= 2),
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: 'System',
          avatar: 'mdi-alert-circle',
        },
      };
      this.currentConversation.push(errorMessage);
    } finally {
      // Centralized finalization - always runs regardless of success/error/early exit
      this.chatLoading = false;
      this.isProcessing = false; // Always unlock after completion

      // Always reset tool execution state to prevent stale indicators
      this.isToolExecuting = false;
      this.currentToolName = null;

      // Clear transient streaming state
      this.streamingEvents = [];
      this.streamingMetadata = null;
    }
  }

  public selectSuggestion(suggestion: string) {
    this.sendMessage(suggestion);
  }

  /**
   * New processor-based sendMessage implementation
   * Uses Strategy Pattern with agent-specific processors
   */
  public async sendMessageWithProcessors(message: string, retryCount: number = 0, metadata?: Record<string, any>) {
    // Prevent multiple simultaneous requests
    if (this.isProcessing) {
      return;
    }

    // Check if an agent is selected
    if (!this.selectedAgent) {
      const systemMessage: ChatMessage = {
        id: this.generateMessageId(),
        content: 'Please select an agent from the Agents tab before starting a conversation.',
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: 'System',
          avatar: 'mdi-information',
        },
      };
      this.currentConversation.push(systemMessage);
      return;
    }

    // Mark as processing
    this.isProcessing = true;
    this.lastMessage = message;

    // Clear stale streaming state
    this.isToolExecuting = false;
    this.currentToolName = null;
    this.streamingEvents = [];
    this.streamingMetadata = null;

    // Add user message to UI
    const userMessage: ChatMessage = {
      id: this.generateMessageId(),
      content: message,
      timestamp: new Date(),
      isUser: true,
    };
    this.currentConversation.push(userMessage);

    // Track query submission for report-builder agent
    if (this.selectedAgent.id === 'report-builder' && this.reportBuilderEventHandler) {
      const userMessages = this.currentConversation.filter((m) => m.isUser);
      this.reportBuilderEventHandler.trackQuerySubmitted({
        queryText: message,
        agentId: this.selectedAgent.id,
        agentName: this.selectedAgent.name,
        conversationType: userMessages.length === 1 ? 'direct_query' : 'conversational',
        messageNumber: userMessages.length,
        sessionId: this.currentConversationId,
      });
    }

    this.chatLoading = true;

    try {
      // Build metadata - merge passed metadata with context
      let requestMetadata: Record<string, any> = metadata ? { ...metadata } : {};

      // Always attach current screen context for ALL agents (automatic context sync)
      const currentContext = contextSyncService.getCurrentContext();
      if (currentContext?.screen) {
        requestMetadata.context = {
          ...requestMetadata.context,
          screen: currentContext.screen,
        };
      }

      // Attach filter context for PO agent (e.g., active performance filter on buying table)
      if (this.isPOAgent() && currentContext?.filters) {
        requestMetadata.context = {
          ...requestMetadata.context,
          filters: currentContext.filters,
        };
      }

      // For PO agent, automatically include displayed products for context sync
      if (this.isPOAgent() && this.poDisplayedProducts.length > 0) {
        requestMetadata.displayed_products = this.poDisplayedProducts;
      }

      // For PO agent, include discussed products (from viewing PO detail)
      if (this.isPOAgent() && this.poDiscussedProducts.length > 0) {
        requestMetadata.discussed_products = this.poDiscussedProducts;
      }

      // Prepare and send API request
      const messageRequest: ChatMessageRequest = {
        message,
        session_id: this.currentConversationId,
        // Include metadata if provided (for context sync)
        ...(Object.keys(requestMetadata).length > 0 && { metadata: requestMetadata }),
      };

      const response = await this.chatCollection.sendMessage(
        this.selectedAgent.id,
        messageRequest,
      );

      // Get the appropriate processor for this agent
      const processor = responseProcessorRegistry.getProcessor(this.selectedAgent.id);
      if (!processor) {
        throw new Error(`No processor found for agent: ${this.selectedAgent.id}`);
      }
      // Normalize the response
      const normalized: NormalizedResponse = processor.normalizeResponse(response);
      // Build processor context
      const context = this.buildProcessorContext();

      // Process the response
      const result: ProcessingResult = await processor.processResponse(normalized, context);

      // Apply the processing result
      await this.applyProcessingResult(result);

      // Update session_id if changed
      if (response.session_id && response.session_id !== this.currentConversationId) {
        this.currentConversationId = response.session_id;
      }

      // Handle routing if present
      if (result.routeNavigation) {
        await this.switchToAgent(result.routeNavigation.targetAgentId);
      }

      // Refresh chat history and save session
      this.refreshChatHistory();
      this.saveSessionToStorage();

    } catch (error: any) {
      // Retry logic for network errors
      if (retryCount < 2 && this.isRetryableError(error)) {
        this.currentConversation.pop(); // Remove user message
        setTimeout(() => {
          this.sendMessageWithProcessors(message, retryCount + 1);
        }, 1000 * (retryCount + 1));
        return;
      }

      // Add error message
      const errorMessage: ChatMessage = {
        id: this.generateMessageId(),
        content: this.getErrorMessage(error, retryCount >= 2),
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: 'System',
          avatar: 'mdi-alert-circle',
        },
      };
      this.currentConversation.push(errorMessage);
    } finally {
      this.chatLoading = false;
      this.isProcessing = false;
      this.isToolExecuting = false;
      this.currentToolName = null;
      this.streamingEvents = [];
      this.streamingMetadata = null;
    }
  }

  /**
   * Build the processor context from current component state
   */
  private buildProcessorContext(): ProcessorContext {
    const userId = (this as any).$store?.state?.user?.id || 'anonymous';
    return {
      // ChatContext properties (inherited)
      sessionId: this.currentConversationId,
      agentId: this.selectedAgent!.id,
      userId,
      // ProcessorContext specific properties
      conversationId: this.currentConversationId,
      selectedAgent: { ...this.selectedAgent! },
      currentConversation: this.currentConversation.map(msg => ({
        id: msg.id,
        content: msg.content,
        timestamp: msg.timestamp,
        isUser: msg.isUser,
        agent: msg.agent,
        isStreaming: msg.isStreaming,
        structuredContent: msg.structuredContent,
      })),
      isContinuedConversation: this.isContinuedConversation,
      conversationStartTime: this.conversationStartTime || Date.now(),
      vueInstance: this,
      generateMessageId: () => this.generateMessageId(),
    };
  }

  /**
   * Apply processing result to the conversation and emit events
   */
  private async applyProcessingResult(result: ProcessingResult): Promise<void> {
    // Remove messages that should be removed
    for (const msgId of result.messagesToRemove) {
      const index = this.currentConversation.findIndex(m => m.id === msgId);
      if (index !== -1) {
        this.currentConversation.splice(index, 1);
      }
    }

    // Add new messages
    for (const msg of result.messagesToAdd) {
      // Convert processor ChatMessage to component ChatMessage
      const chatMessage: ChatMessage = {
        id: msg.id,
        content: msg.content,
        timestamp: msg.timestamp,
        isUser: msg.isUser,
        agent: msg.agent,
        isStreaming: msg.isStreaming,
        structuredContent: msg.structuredContent,
      };
      this.currentConversation.push(chatMessage);
    }

    // Emit EventBus events
    if (result.eventBusEmits) {
      for (const emit of result.eventBusEmits) {
        EventBus.$emit(emit.event, emit.payload);
      }
    }

    // Force Vue update if needed
    if (result.messagesToAdd.length > 0 || result.messagesToRemove.length > 0) {
      this.$forceUpdate();
    }
  }

  public createNewChat() {
    this.currentConversation = [];
    this.currentConversationId = this.generateConversationId();
    this.isContinuedConversation = false; // Mark as new conversation
    this.handoffInProgress = false; // Reset handoff flag
    // this.selectedAgent = null; // Clear selected agent
    this.activeTab = 0; // Switch to chat tab
    // Set session ID for context sync service
    contextSyncService.setSessionId(this.currentConversationId);

    // Clear saved session since we're starting fresh
    this.clearSavedSession();
  }

  public updateHistorySearch(query: string) {
    this.historySearchQuery = query;
  }

  public refreshChatHistory() {
    // Refresh the ChatHistory component to fetch updated data
    const chatHistoryComponent = this.$refs.chatHistory as any;
    if (chatHistoryComponent && chatHistoryComponent.refreshHistory) {
      chatHistoryComponent.refreshHistory();
    }
  }

  public selectConversation(conversation: IChatConversation) {
    // Check for unsaved changes before switching conversations
    if (this.hasUnsavedMessage && this.pendingMessage.trim()) {
      this.pendingNavigationCallback = () => {
        this.loadConversation(conversation);
      };
      this.showNavigationWarning = true;
      return;
    }

    this.loadConversation(conversation);
  }

  public deleteConversation(conversationId: string) {
    this.chatHistory = this.chatHistory.filter((c) => c.id !== conversationId);
  }

  public handleSwitchAgent(agentId: string) {

    // Find the agent by ID
    const agent = this.availableAgents.find((a) => a.id === agentId);
    if (agent) {
      this.selectAgent(agent); // This will save the agent to localStorage
    } else if (agentId === 'orchestrator') {
      // Handle orchestrator agent specially if needed
      const orchestratorAgent: Agent = {
        id: 'orchestrator',
        name: 'Orchestrator',
        description: 'Helps you find the right specialist agent',
        avatar: 'mdi-assistant',
        category: 'General',
        isRecommended: true,
        isFavorite: false,
        creator: 'System',
      };
      this.selectAgent(orchestratorAgent); // This will save the agent to localStorage
    }
  }

  public selectAgent(agent: Agent) {
    // Store previous agent for tracking
    const previousAgent = this.selectedAgent;

    // Clear current conversation and generate new conversation ID
    this.currentConversation = [];
    this.currentConversationId = this.generateConversationId();
    this.activeTab = 0; // Switch to chat tab
    // Set session ID for context sync service
    contextSyncService.setSessionId(this.currentConversationId);

    // Initialize conversation start time for tracking
    this.conversationStartTime = Date.now();

    // Set the selected agent for chat context AFTER clearing conversation
    this.selectedAgent = agent;

    // Save the selected agent to localStorage for persistence
    this.saveLastSelectedAgent(agent);

    // Emit agent selection tracking event for Report Builder (Phase 2)
    if (agent.id === 'report-builder') {
      EventBus.$emit('report-builder:agent-selected', {
        agentId: agent.id,
        agentName: agent.name,
        selectionMethod: 'manual',
        previousAgent: previousAgent?.id,
        isNewConversation: true,
      });
    }

    // Add welcome message from the selected agent
    const welcomeMessage: ChatMessage = {
      id: this.generateMessageId(),
      content: `Hi! I'm ${agent.name}. ${agent.description}`,
      timestamp: new Date(),
      isUser: false,
      agent: {
        name: agent.name,
        avatar: agent.avatar,
      },
    };
    this.currentConversation.push(welcomeMessage);

    // Clear saved session since we're starting a new conversation
    this.clearSavedSession();

    // Auto-navigate to Report Builder page if needed
    if (agent.id === 'report-builder' && this.$route.path !== '/assistant/ai-report-builder') {
      // Save session before navigation so it can be restored
      this.saveSessionToStorage();

      // Navigate to dedicated Report Builder page
      this.$router.push('/assistant/ai-report-builder');
    }

    // Auto-navigate to Purchase Assistant page if needed
    if (agent.id === 'purchase-order' && this.$route.path !== '/purchase-assistant') {
      // Save session before navigation so it can be restored
      this.saveSessionToStorage();

      // Navigate to dedicated Purchase Assistant page
      this.$router.push('/purchase-assistant');

      // Re-emit chatbox:opened so PurchaseAssistant knows chatbox is open
      EventBus.$emit('chatbox:opened');
    }
  }

  public createAgent() {
    // TODO: Implement agent creation
  }

  public toggleFullscreen() {
    this.$emit('toggle-fullscreen');
  }

  public closeChatbox() {
    // Emit close event with interaction count (total messages in conversation)
    const interactionCount = this.currentConversation?.length || 0;
    this.$emit('close-chatbox', { interactionCount });
  }

  /**
   * Load agents from the backend API
   */
  public async loadAgents() {
    this.agentsLoading = true;
    this.agentsError = null;

    try {
      const agentResponses = await this.agentsCollection.getAgents();
      this.availableAgents = agentResponses.map(mapAgentResponse);

      // Filter out Purchase Order Assistant if not logged in from Hades (internal team only)
      if (!this.isLoggedInFromHades) {
        this.availableAgents = this.availableAgents.filter(
          agent => agent.id !== 'purchase-order'
        );
      }

      // Apply locally stored favorites
      this.applyLocalFavorites();
    } catch (error: any) {
      this.agentsError = error.message || 'Failed to load agents';
      // Fallback to sample data if API fails
      this.loadFallbackAgents();
    } finally {
      this.agentsLoading = false;
    }
  }

  /**
   * Toggle agent favorite status (local only - backend doesn't support this)
   */
  public async toggleAgentFavorite(agentId: string) {
    const agent = this.availableAgents.find((a) => a.id === agentId);
    if (!agent) { return; }

    // Toggle local state only since backend doesn't support favorites
    agent.isFavorite = !agent.isFavorite;

    // Save to localStorage for persistence across sessions
    const favorites = JSON.parse(localStorage.getItem('agent_favorites') || '[]');
    if (agent.isFavorite) {
      if (!favorites.includes(agentId)) {
        favorites.push(agentId);
      }
    } else {
      const index = favorites.indexOf(agentId);
      if (index > -1) {
        favorites.splice(index, 1);
      }
    }
    localStorage.setItem('agent_favorites', JSON.stringify(favorites));
  }

  /**
   * Refresh agents data
   */
  public async refreshAgents() {
    await this.loadAgents();
  }

  public onInputChanged(value: string) {
    this.pendingMessage = value;
    this.hasUnsavedMessage = value.trim().length > 0;
  }

  public confirmNavigation() {
    this.showNavigationWarning = false;
    this.hasUnsavedMessage = false;
    this.pendingMessage = '';

    if (this.pendingNavigationCallback) {
      this.pendingNavigationCallback();
      this.pendingNavigationCallback = null;
    }
  }

  /**
   * Retry the last failed message (called from ErrorBoundary)
   */
  public retryLastMessage() {
    if (this.lastMessage && this.selectedAgent) {
      this.sendMessage(this.lastMessage);
    }
  }

  /**
   * Select agent by ID programmatically (triggered via EventBus)
   * @param payload - Either a string agentId or an object with agentId property
   */
  public async selectAgentById(payload: string | { agentId: string } | null | undefined): Promise<void> {
    // Normalize payload to extract agentId (handles both string and object formats)
    const agentId = typeof payload === 'string' ? payload : payload?.agentId;

    if (!agentId) {
      return;
    }

  // Ensure agents are loaded using shared helper
    if (!await this.ensureAgentsLoaded()) {
      return;
    }

    // Find and select the agent
    const agentToSelect = this.availableAgents.find((a) => a.id === agentId);
    if (agentToSelect) {
      this.selectAgent(agentToSelect);
    }
  }

  /**
   * Send message programmatically (triggered via EventBus)
   * Supports optional metadata for context synchronization (e.g., displayed_products from Purchase Assistant)
   */
  public sendMessageProgrammatically(payload: { message: string; metadata?: Record<string, any> }) {
    if (payload.message && payload.message.trim()) {
      this.sendMessage(payload.message, 0, payload.metadata);
    }
  }

  /**
   * Handle Report Builder compound query command
   * Ensures proper sequencing: open chatbox -> select agent -> send message
   * This provides atomic operation without timing issues
   */
  public async handleReportBuilderQuery(payload: { query: string, timestamp: number }) {

    try {
      // Ensure chatbox component is ready
      await this.$nextTick();

      // Ensure agents are loaded using shared helper
      if (!await this.ensureAgentsLoaded()) {
        EventBus.$emit('report-builder:query-complete', {
          success: false,
          error: 'Agents not available. Please refresh and try again.',
        });
        return;
      }

      // Find Report Builder agent
      const reportBuilderAgent = this.availableAgents.find((a) => a.id === 'report-builder');

      if (!reportBuilderAgent) {
        EventBus.$emit('report-builder:query-complete', {
          success: false,
          error: 'Report Builder agent not available',
        });
        return;
      }

      // Select the Report Builder agent
      this.selectAgent(reportBuilderAgent);

      // Wait for selection to complete and UI to update
      await this.$nextTick();
      await new Promise((resolve) => setTimeout(resolve, 100)); // Small delay for DOM update

      // Verify agent is selected
      if (!this.selectedAgent || this.selectedAgent.id !== 'report-builder') {
        EventBus.$emit('report-builder:query-complete', {
          success: false,
          error: 'Failed to select Report Builder agent',
        });
        return;
      }

      // Send the message - await to ensure it starts processing
      await this.sendMessage(payload.query);

      // Report success - the query is being processed
      EventBus.$emit('report-builder:query-complete', {
        success: true,
        timestamp: payload.timestamp,
      });

    } catch (error: any) {
      EventBus.$emit('report-builder:query-complete', {
        success: false,
        error: error.message || 'Unknown error occurred',
      });
    }
  }

  /**
   * Clean up EventBus listeners
   */
  public beforeDestroy() {
    // Clear chatbox open status
    localStorage.setItem('assisty_chatbox_open', 'false');

    EventBus.$off('chatbox:select-agent', this.selectAgentById);
    EventBus.$off('chatbox:send-message', this.sendMessageProgrammatically);
    EventBus.$off('chatbox:check-status', this.handleStatusCheck);

    // Remove the report builder query handler using the stored reference
    if (this.reportBuilderQueryHandler) {
      EventBus.$off('chatbox:report-builder-query', this.reportBuilderQueryHandler);
    }

    // Remove PO Agent products loaded listener
    EventBus.$off('po-builder:products-loaded', this.handlePOProductsLoaded);

    // Remove PO Agent discussed products loaded listener
    EventBus.$off('po-builder:discussed-products-loaded', this.handlePODiscussedProductsLoaded);
  }

  /**
   * Handle products loaded from Purchase Assistant's BuyingTableDisplay
   * Stores products for automatic inclusion in PO agent chat requests
   */
  private handlePOProductsLoaded(payload: {
    products: Array<Record<string, any>>;
    supplierId?: string;
    supplierName?: string;
    totalCount?: number;
  }) {
    this.poDisplayedProducts = payload.products || [];

    // Sync reorder suggestions to backend context API
    if (this.currentConversationId && payload.products && payload.products.length > 0) {
      contextSyncService.updateDataContext('reorder_suggestions', payload.products, {
        totalCount: payload.totalCount,
      });
    }
  }

  /**
   * Handle discussed products loaded from Purchase Assistant's PurchaseOrderDisplay
   * Stores products for automatic inclusion in PO agent chat requests
   * This enables "create draft with same items" workflow when viewing a PO
   */
  private handlePODiscussedProductsLoaded(payload: {
    products: Array<Record<string, any>>;
    source?: string;
    po_id?: number;
    po_number?: string;
    supplier_id?: number;
    supplier_name?: string;
  }) {
    this.poDiscussedProducts = payload.products || [];

    // Sync discussed products to backend context API
    if (this.currentConversationId && payload.products && payload.products.length > 0) {
      contextSyncService.updateDataContext('discussed_products', payload.products, {
        totalCount: payload.products.length,
      });
    }
  }

  private loadConversation(conversation: IChatConversation) {
    // Ensure messages is an array
    this.currentConversation = conversation.messages || [];
    this.currentConversationId = conversation.id;
    this.isContinuedConversation = true; // Mark as continued conversation
    this.handoffInProgress = false; // Reset handoff flag for continued conversation
    // Set session ID for context sync service
    contextSyncService.setSessionId(conversation.id);

    // Restore agent context if available
    if (conversation.agentId && conversation.agentName) {
      // Find the agent in available agents or create a basic agent object
      const agent = this.availableAgents.find((a) => a.id === conversation.agentId) || {
        id: conversation.agentId,
        name: conversation.agentName,
        description: 'Previous conversation agent',
        avatar: 'mdi-assistant',
        category: 'All Agents',
        isRecommended: false,
        isFavorite: false,
        creator: 'Unknown',
      };
      this.selectedAgent = agent;

      // Save the selected agent to localStorage for persistence
      this.saveLastSelectedAgent(agent);
    }

    // Clear unsaved state
    this.hasUnsavedMessage = false;
    this.pendingMessage = '';

    this.activeTab = 0; // Switch to chat tab

    // Save loaded session to localStorage for persistence
    this.saveSessionToStorage();
  }

  /**
   * Apply locally stored favorite status to agents
   */
  private applyLocalFavorites() {
    const favorites = JSON.parse(localStorage.getItem('agent_favorites') || '[]');
    this.availableAgents.forEach((agent) => {
      agent.isFavorite = favorites.includes(agent.id);
    });
  }

  /**
   * Fallback sample data if API fails
   */
  private loadFallbackAgents() {
    this.availableAgents = [
      {
        id: 'fallback-1',
        name: 'Work Organizer',
        description: 'Quickly find, update, and manage tasks in your project. Move work into sprints, group related tasks, clean up outdated items, and prioritize based on dependencies.',
        avatar: 'mdi-assistant',
        category: 'Recommended',
        isRecommended: true,
        isFavorite: false,
        creator: 'Atlassian',
      },
      {
        id: 'fallback-2',
        name: 'Progress Tracker',
        description: 'Get a real-time overview of in-flight projects and their priorities. Ensure that teams have visibility into what\'s currently being worked on.',
        avatar: 'mdi-assistant',
        category: 'Recommended',
        isRecommended: true,
        isFavorite: false,
        creator: 'Atlassian',
      },
      {
        id: 'fallback-3',
        name: 'Dashboard Insights',
        description: 'Generates insights for Home dashboards.',
        avatar: 'mdi-assistant',
        category: 'Recommended',
        isRecommended: true,
        isFavorite: false,
        creator: 'Atlassian',
      },
    ];

    // Apply local favorites to fallback data as well
    this.applyLocalFavorites();
  }

  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateConversationId(): string {
    return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clean response content for better UX (fallback method)
   */
  private cleanResponseContent(rawReply: string): string {
    // Check if this is a RunResult debug output
    if (rawReply.includes('RunResult:') && rawReply.includes('Final output')) {
      // Extract content between "Final output (str): " and the next " - "
      const finalOutputMatch = rawReply.match(/Final output \(str\):\s*([^-]+?)(?:\s*-|\s*$)/);
      if (finalOutputMatch && finalOutputMatch[1]) {
        return finalOutputMatch[1].trim();
      }

      // Fallback: try to extract any quoted content that looks like a message
      const quotedMatch = rawReply.match(/"([^"]+)"/);
      if (quotedMatch && quotedMatch[1] && quotedMatch[1].length > 10) {
        return quotedMatch[1];
      }
    }

    // If it doesn't look like debug output, return as-is
    return rawReply;
  }

  /**
   * Check if an error is retryable (network/timeout errors)
   */
  private isRetryableError(error: any): boolean {
    // Retry on network errors or timeouts
    return (
      error.message.includes('Network Error') ||
      error.message.includes('timeout') ||
      error.code === 'ECONNABORTED' ||
      (error.response && error.response.status >= 500)
    );
  }

  /**
   * Generate user-friendly error messages based on error type
   */
  private getErrorMessage(error: any, afterRetries: boolean): string {
    const baseMessage = afterRetries ? 'After multiple attempts, I ' : 'I ';

    if (error.message.includes('Network Error')) {
      return `${baseMessage}couldn't connect to the chat service. Please check your internet connection and try again.`;
    } else if (error.message.includes('timeout')) {
      return `${baseMessage}didn't receive a response in time. The service might be busy. Please try again.`;
    } else if (error.response?.status === 401) {
      return `${baseMessage}encountered an authentication error. Please refresh the page and try again.`;
    } else if (error.response?.status === 403) {
      return `${baseMessage}don't have permission to access this feature. Please contact support.`;
    } else if (error.response?.status === 429) {
      return `${baseMessage}encountered a rate limit. Please wait a moment before trying again.`;
    } else if (error.response?.status >= 500) {
      return `${baseMessage}encountered a server error. Our team has been notified. Please try again later.`;
    } else {
      return `${baseMessage}encountered an unexpected error: ${error.message}. Please try again.`;
    }
  }

  private generateAIResponse(userMessage: string): string {
    // Simple response generation - replace with actual AI integration
    const responses = [
      'I understand you\'d like help with that. Let me analyze your request.',
      'Based on your dashboard data, here are some insights I can provide.',
      'I can help you with that task. Let me break it down for you.',
      'That\'s a great question! Here\'s what I recommend based on your project data.',
      'I\'ll help you organize and prioritize that information.',
    ];
    return responses[Math.floor(Math.random() * responses.length)];
  }

  /**
   * Detect if a message is a clarification request from the agent
   * Uses targeted phrases and patterns to avoid false positives
   */
  private isClarificationRequest(message: { isUser: boolean; content: string }): boolean {
    // Skip user messages - only check agent responses
    if (message.isUser) {
      return false;
    }

    const content = message.content.toLowerCase();

    // Clarification request phrases (case-insensitive)
    const clarificationPhrases = [
      'could you clarify',
      'please clarify',
      'can you clarify',
      'need more information',
      'need more details',
      'can you provide more details',
      'can you provide more information',
      'could you provide more',
      'please provide more',
      'which one do you mean',
      'which did you mean',
      'what do you mean by',
      'can you be more specific',
      'could you be more specific',
      'please be more specific',
      'not sure what you mean',
      'unclear what you',
      "i'm not sure which",
      "i don't understand which",
      'could you specify',
      'please specify',
      'can you specify',
    ];

    // Check for exact phrase matches
    for (const phrase of clarificationPhrases) {
      if (content.includes(phrase)) {
        return true;
      }
    }

    // Regex patterns for common clarification request structures
    const clarificationPatterns = [
      /\b(which|what)\s+(product|item|report|metric|dimension|measure)\s+(do you|did you|are you)\b/i,
      /\b(do you mean|did you mean)\b/i,
      /\bcan you (tell me|let me know|confirm|elaborate)\b/i,
      /\bcould you (tell me|let me know|confirm|elaborate)\b/i,
      /\bplease (tell me|let me know|confirm|elaborate|explain)\b/i,
    ];

    // Check regex patterns
    for (const pattern of clarificationPatterns) {
      if (pattern.test(message.content)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Ensure agents are loaded, wait if loading, trigger load if needed
   * Returns true if agents are available, false otherwise
   */
  private async ensureAgentsLoaded(maxWaitMs: number = 5000): Promise<boolean> {
    // If agents already loaded, return immediately
    if (this.availableAgents.length > 0) {
      return true;
    }

    // If agents are currently loading, wait for completion
    if (this.agentsLoading) {
      const startTime = Date.now();
      while (this.agentsLoading && (Date.now() - startTime) < maxWaitMs) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      // Check if agents loaded successfully
      return this.availableAgents.length > 0;
    }

    // If not loading and not loaded, trigger load
    try {
      await this.loadAgents();
      return this.availableAgents.length > 0;
    } catch (error) {
      console.error('[AgentChatbox] Error loading agents:', error);
      return false;
    }
  }

  /**
   * Auto-select the orchestrator agent as default
   */
  private async autoSelectOrchestrator(agentId: string = 'orchestrator') {
    // Wait for agents to be loaded
    if (this.availableAgents.length === 0) {
      // If no agents loaded yet, wait a bit and try again
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    // Find the orchestrator agent
    const orchestrator = this.availableAgents.find((agent) =>
      agent.id === agentId,
    );

    if (orchestrator) {
      this.selectAgent(orchestrator);
    }
  }

  /**
   * Switch to a different agent based on routing response
   */
  private async switchToAgent(targetAgentId: string) {
    // Find the target agent
    const targetAgent = this.availableAgents.find((agent) => agent.id === targetAgentId);

    if (targetAgent) {
      // Don't clear conversation - keep the routing context
      this.selectedAgent = targetAgent;

      // Save the selected agent to localStorage for persistence
      this.saveLastSelectedAgent(targetAgent);

      // Add a welcome message from the new agent
      const switchMessage: ChatMessage = {
        id: this.generateMessageId(),
        content: `Hi! I'm ${targetAgent.name}, your ${targetAgent.category} specialist. How can I help you today?`,
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: targetAgent.name,
          avatar: targetAgent.avatar,
        },
      };
      this.currentConversation.push(switchMessage);
    } else {
      // Add error message
      const errorMessage: ChatMessage = {
        id: this.generateMessageId(),
        content: `Sorry, I couldn't connect you to the requested specialist. Please try selecting an agent manually from the Agents tab.`,
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: 'System',
          avatar: 'mdi-alert-circle',
        },
      };
      this.currentConversation.push(errorMessage);
    }
  }

  /**
   * Detect if response uses new ChatResponse format (events-only architecture)
   * Detection is based on response.reply being an object with an events array
   */
  private isNewChatResponseFormat(response: any): boolean {
    return response.reply &&
           typeof response.reply === 'object' &&
           Array.isArray(response.reply.events);
  }

  /**
   * Process events from new ChatResponse format (Report Builder, Purchase Order)
   */
  private processNewFormatEvents(events: StreamingEvent[], chatResponse?: any): void {
    // Find preview_canvas_action event
    const previewEvent = events.find((e) => e.type === 'preview_canvas_action');

    if (previewEvent && previewEvent.metadata) {
      const actionName = previewEvent.metadata.action;
      // Validate payload is an object before accessing properties
      const rawPayload = previewEvent.metadata.payload;
      const actionPayload = (rawPayload && typeof rawPayload === 'object' ? rawPayload : {}) as Record<string, any>;

      if (actionName === 'show_report_preview') {
        const extractedParams = actionPayload.extracted_parameters || actionPayload;

        // Call existing handleReportPreviewAction method
        this.handleReportPreviewAction(extractedParams, {
          sessionId: this.currentConversationId,
          agentId: this.selectedAgent?.id || 'unknown',
          userId: (this as any).$store?.state?.user?.id || 'anonymous',
        });

        // Track conversation quality for report-builder
        if (this.selectedAgent?.id === 'report-builder') {
          const userMessages = this.currentConversation.filter((m) => m.isUser);
          const clarificationMessages = this.currentConversation.filter((m) =>
            this.isClarificationRequest(m),
          );

          EventBus.$emit('report-builder:conversation-quality', {
            agentId: this.selectedAgent.id,
            sessionId: this.currentConversationId,
            messageCount: this.currentConversation.length,
            clarificationRequestsCount: clarificationMessages.length,
            successfulInterpretation: true,
            conversationDuration: this.conversationStartTime
              ? (Date.now() - this.conversationStartTime) / 1000
              : 0,
            queryRefinementIterations: userMessages.length - 1,
          });
        }
      }
    }

    // Handle action events (e.g., live_chat_handoff, show_live_chat_button, canvas actions)
    // agent_handoff events are internal to the multi-agent system and should be ignored
    // human_handoff events are deprecated - use action events instead
    const actionEvent = events.find((e) => e.type === 'action');
    if (actionEvent && actionEvent.metadata && actionEvent.metadata.action) {
      const action = actionEvent.metadata.action;

      if (action === 'live_chat_handoff') {
        // Trigger handoff to live chat (auto-trigger - old behavior)
        this.checkForHandoffRequest({
          metadata: {
            needs_human_handoff: true,
            handoff_reason: actionEvent.metadata.reason || 'user_request',
            handoff_count: actionEvent.metadata.handoff_count || 1,
          }
        } as any);
      } else if (action === 'show_live_chat_button') {
        // Add a special message with button metadata
        const buttonMessage: ChatMessage = {
          id: this.generateMessageId(),
          content: String(actionEvent.metadata.button_text || 'Connect to Live Support'),
          timestamp: new Date(),
          isUser: false,
          agent: {
            name: 'System',
            avatar: 'mdi-human-greeting',
          },
          structuredContent: [{
            type: 'live_chat_button',
            buttonText: String(actionEvent.metadata.button_text || 'Connect to Live Support'),
            buttonDescription: String(actionEvent.metadata.button_description || 'Click to connect with our support team'),
            ticketKey: actionEvent.metadata.ticket_key || null,
            metadata: actionEvent.metadata || {}
          }]
        };

        this.currentConversation.push(buttonMessage);
        this.$forceUpdate();
      } else if (action === 'SHOW_REPORT_PREVIEW') {
        // Handle Report Builder preview action (standardized format)
        const extractedParams = actionEvent.metadata?.parameters || {};

        // Call existing handleReportPreviewAction method
        this.handleReportPreviewAction(extractedParams, {
          sessionId: this.currentConversationId,
          agentId: this.selectedAgent?.id || 'report-builder',
          userId: (this as any).$store?.state?.user?.id || 'anonymous',
        });

        // Track conversation quality for report-builder
        if (this.selectedAgent?.id === 'report-builder') {
          const userMessages = this.currentConversation.filter((m) => m.isUser);
          const clarificationMessages = this.currentConversation.filter((m) =>
            this.isClarificationRequest(m),
          );

          EventBus.$emit('report-builder:conversation-quality', {
            agentId: this.selectedAgent.id,
            sessionId: this.currentConversationId,
            messageCount: this.currentConversation.length,
            clarificationRequestsCount: clarificationMessages.length,
            successfulInterpretation: true,
            conversationDuration: this.conversationStartTime
              ? (Date.now() - this.conversationStartTime) / 1000
              : 0,
            queryRefinementIterations: userMessages.length - 1,
          });
        }
      } else if (isCanvasAction(action)) {
        // Handle PO Agent and other canvas actions from events
        const context: ChatContext = {
          sessionId: this.currentConversationId,
          agentId: this.selectedAgent?.id || 'unknown',
          userId: (this as any).$store?.state?.user?.id || 'anonymous',
        };

        // Use setTimeout to ensure UI is ready
        setTimeout(() => {
          // Embed actions array inside chat_response for canvas component consumption
          executeAgentAction('show_po_agent_preview', {
            chat_response: {
              ...(chatResponse || {}),
              actions: [{
                action: action,
                parameters: actionEvent.metadata?.parameters || {},
                agent: (actionEvent as any).agent || this.selectedAgent?.name || 'Agent'
              }]
            }
          }, context);
        }, 100);
      }
    }
  }

  /**
   * Check for human handoff request in response metadata
   */
  private checkForHandoffRequest(response: ChatMessageResponse): void {

    if (response.metadata?.needs_human_handoff) {
      // Prevent duplicate handoffs
      if (this.handoffInProgress) {
        return;
      }

      this.handoffInProgress = true;

      // Add "connecting to human" message to chat
      const connectingMessage: ChatMessage = {
        id: this.generateMessageId(),
        content: 'Connecting you with a human expert from our support team...',
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: 'System',
          avatar: 'mdi-human-greeting',
        },
        isStreaming: true, // Show typing indicator
      };
      this.currentConversation.push(connectingMessage);

      const handoffData = {
        agentId: this.selectedAgent?.id || 'unknown',
        agentName: this.selectedAgent?.name || 'Unknown Agent',
        conversationId: this.currentConversationId,
        metadata: response.metadata,
        timestamp: new Date().toISOString(),
      };
      // Emit EventBus event to trigger Crisp chat opening
      EventBus.$emit('open-crisp-for-handoff', handoffData);

      // Clear streaming indicator after a short delay (Crisp is opening)
      setTimeout(() => {
        const msgIndex = this.currentConversation.findIndex(m => m.id === connectingMessage.id);
        if (msgIndex !== -1) {
          this.currentConversation[msgIndex].isStreaming = false;
          this.$set(this.currentConversation, msgIndex, { ...this.currentConversation[msgIndex] });
        }
        this.handoffInProgress = false;
      }, 2000); // 2 seconds to show the loading indicator
    }
  }

  /**
   * Show notification to user that live chat is being opened
   * Note: Actual notification is now handled by ChatConversation.vue with 10s delay
   */
  private showHandoffNotification(): void {
    // Notification is now shown in ChatConversation.vue during the 10-second delay
    // This method is kept for backwards compatibility but does nothing
  }

  /**
   * Save current session to localStorage for persistence across browser reloads
   */
  private saveSessionToStorage() {
    if (!this.currentConversationId || !this.selectedAgent) {
      return;
    }

    const sessionData = {
      sessionId: this.currentConversationId,
      agentId: this.selectedAgent.id,
      timestamp: Date.now(),
    };

    localStorage.setItem('assisty_active_session', JSON.stringify(sessionData));
  }

  /**
   * Restore previous session from localStorage if it exists and is valid
   * Returns true if session was restored, false otherwise
   */
  private async restorePreviousSession(): Promise<boolean> {
    try {
      const savedSessionJson = localStorage.getItem('assisty_active_session');
      if (!savedSessionJson) {
        return false;
      }

      const savedSession = JSON.parse(savedSessionJson);
      const { sessionId, agentId, timestamp } = savedSession;

      // Check if session is less than 24 hours old
      const sessionAge = Date.now() - timestamp;
      const twentyFourHours = 24 * 60 * 60 * 1000;

      if (sessionAge > twentyFourHours) {
        localStorage.removeItem('assisty_active_session');
        return false;
      }

      // Import ChatHistoryCollection
      const ChatHistoryCollection = (await import('@/collections/chatHistory')).default;
      const chatHistoryCollection = new ChatHistoryCollection();

      // Try to load the session from backend to validate it still exists
      const sessionDetail = await chatHistoryCollection.getSessionHistory(sessionId);

      if (!sessionDetail) {
        localStorage.removeItem('assisty_active_session');
        return false;
      }

      // Find the agent
      const agent = this.availableAgents.find((a) => a.id === agentId);
      if (!agent) {
        localStorage.removeItem('assisty_active_session');
        return false;
      }

      // Restore agent
      this.selectedAgent = agent;

      // Save the selected agent to localStorage for persistence
      this.saveLastSelectedAgent(agent);

      // Convert backend messages to frontend format
      const messages = (sessionDetail.messages || []).map((msg, index) => ({
        id: msg.id || `msg-${index}-${msg.timestamp}`,
        content: this.cleanResponseContent(msg.content),
        timestamp: new Date(msg.timestamp * 1000),
        isUser: msg.role === 'user',
        agent: msg.role === 'assistant' ? {
          name: sessionDetail.agent_name,
          avatar: agent.avatar,
        } : undefined,
      }));

      // Restore conversation
      this.currentConversation = messages;
      this.currentConversationId = sessionId;
      this.isContinuedConversation = true;
      // Set session ID for context sync service
      contextSyncService.setSessionId(sessionId);

      return true;

    } catch (error) {
      localStorage.removeItem('assisty_active_session');
      return false;
    }
  }

  /**
   * Clear saved session from localStorage
   */
  private clearSavedSession() {
    localStorage.removeItem('assisty_active_session');
  }

  /**
   * Save the last selected agent to localStorage for persistence
   */
  private saveLastSelectedAgent(agent: Agent) {
    const agentData = {
      id: agent.id,
      name: agent.name,
      description: agent.description,
      avatar: agent.avatar,
      category: agent.category,
      timestamp: Date.now(),
    };
    localStorage.setItem('assisty_last_selected_agent', JSON.stringify(agentData));
  }

  /**
   * Restore the last selected agent from localStorage
   * Returns true if an agent was restored, false otherwise
   */
  private async restoreLastSelectedAgent(): Promise<boolean> {
    try {
      const savedAgentJson = localStorage.getItem('assisty_last_selected_agent');
      if (!savedAgentJson) {
        return false;
      }

      const savedAgent = JSON.parse(savedAgentJson);
      const { id, timestamp } = savedAgent;

      // Check if saved agent selection is less than 7 days old
      const agentAge = Date.now() - timestamp;
      const sevenDays = 7 * 24 * 60 * 60 * 1000;

      if (agentAge > sevenDays) {
        localStorage.removeItem('assisty_last_selected_agent');
        return false;
      }

      // Find the agent in available agents
      const agent = this.availableAgents.find((a) => a.id === id);
      if (!agent) {
        localStorage.removeItem('assisty_last_selected_agent');
        return false;
      }

      // Restore the agent without clearing conversation
      this.selectedAgent = agent;

      // Add welcome message from the selected agent
      const welcomeMessage: ChatMessage = {
        id: this.generateMessageId(),
        content: `Hi! I'm ${agent.name}. ${agent.description}`,
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: agent.name,
          avatar: agent.avatar,
        },
      };
      this.currentConversation.push(welcomeMessage);

      return true;
    } catch (error) {
      localStorage.removeItem('assisty_last_selected_agent');
      return false;
    }
  }

  /**
   * Remove action loading message from the conversation
   */
  private removeActionLoadingMessage(actionName: string) {
    const messageId = this.actionLoadingMessageIds.get(actionName);

    if (messageId) {
      const messageIndex = this.currentConversation.findIndex((m) => m.id === messageId);

      if (messageIndex !== -1) {
        this.currentConversation.splice(messageIndex, 1);
      }
      this.actionLoadingMessageIds.delete(actionName);
    }
  }

  /**
   * Handle report preview action with auto-navigation
   * Navigates to Report Builder page if not already there
   */
  private async handleReportPreviewAction(payload: any, context: ChatContext) {

    // Check if we're on the Report Builder page
    const isOnReportBuilderPage = this.$route.path === '/assistant/ai-report-builder' || this.$route.name === 'ReportBuilder';

    if (!isOnReportBuilderPage) {

      // Navigate to Report Builder page
      await this.$router.push('/assistant/ai-report-builder');

      // Wait for navigation and component mounting
      await this.$nextTick();

      // Re-emit chatbox:opened event so the newly mounted ReportBuilder component knows chatbox is open
      EventBus.$emit('chatbox:opened');

      // Add a small delay to ensure ReportBuilder component is fully mounted and listening
      setTimeout(() => {
        executeAgentAction('show_report_preview', { extracted_parameters: payload }, context);
      }, 300);
    } else {

      // Make sure payload is in the correct format for the action handler
      const actionPayload = payload.extracted_parameters ? payload : { extracted_parameters: payload };

      setTimeout(() => {
        executeAgentAction('show_report_preview', actionPayload, context);
      }, 100);
    }
  }

  /**
   * Handle chatbox status check request
   * Responds with chatbox:ready to signal that chatbox is open and ready
   */
  private handleStatusCheck = () => {
    // Respond that chatbox is open
    localStorage.setItem('assisty_chatbox_open', 'true');
    // Emit chatbox:ready to notify waiting components (e.g., ReportBuilder)
    EventBus.$emit('chatbox:ready');
  }
}
